/**
 * JobDetailPage.jsx
 * Path: frontend/src/pages/JobDetailPage.jsx
 * Description: Szczegóły zlecenia z edycją, multimediami i komunikatorem.
 * Related: api/jobs.js, components/AttachmentList.jsx, components/JobChat.jsx
 */

import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import AppShell from "../components/AppShell";
import AttachmentList from "../components/AttachmentList";
import JobChat from "../components/JobChat";
import JobFormModal from "../components/JobFormModal";
import { AuthContext } from "../context/AuthContext";
import {
  deleteJobAttachment,
  deleteJob,
  downloadJobAttachment,
  editJobMessage,
  getJob,
  sendJobMessage,
  updateJob,
  uploadJobAttachment,
} from "../api/jobs";
import { listActiveUsers } from "../api/users";
import { toPolishDateTime } from "../utils/date";

export default function JobDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { token, user } = useContext(AuthContext);

  const [job, setJob] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [deletingJob, setDeletingJob] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState("");
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [jobData, userData] = await Promise.all([
        getJob(token, id),
        listActiveUsers(token),
      ]);
      setJob(jobData);
      setUsers(userData);
    } catch (err) {
      setError(err?.response?.data?.error || "Nie udało się pobrać zlecenia.");
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  async function handleSave(payload) {
    setSaving(true);
    try {
      await updateJob(token, id, payload);
      setEditing(false);
      await loadData();
    } finally {
      setSaving(false);
    }
  }

  async function handleUpload(files) {
    setUploading(true);
    try {
      const queue = Array.isArray(files) ? files : [files];
      for (const file of queue) {
        await uploadJobAttachment(token, id, file);
      }
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || "Nie udało się wysłać pliku.");
    } finally {
      setUploading(false);
    }
  }

  async function handleDeleteAttachment(attachment) {
    setDeletingAttachmentId(attachment.id);
    try {
      await deleteJobAttachment(token, id, attachment.id);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || "Nie udało się usunąć pliku.");
    } finally {
      setDeletingAttachmentId("");
    }
  }

  async function handleSendMessage(payload) {
    setSending(true);
    try {
      await sendJobMessage(token, id, payload);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || "Nie udało się wysłać wiadomości.");
      throw err;
    } finally {
      setSending(false);
    }
  }

  async function handleEditMessage(messageId, payload) {
    setSending(true);
    try {
      await editJobMessage(token, id, messageId, payload);
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || "Nie udało się zapisać zmian w wiadomości.");
      throw err;
    } finally {
      setSending(false);
    }
  }

  async function handleDeleteJob() {
    if (!job) {
      return;
    }

    const confirmed = window.confirm(`Usunąć zlecenie "${job.title}"?`);
    if (!confirmed) {
      return;
    }

    setDeletingJob(true);
    try {
      await deleteJob(token, id);
      navigate("/calendar");
    } catch (err) {
      setError(err?.response?.data?.error || "Nie udało się usunąć zlecenia.");
    } finally {
      setDeletingJob(false);
    }
  }

  return (
    <AppShell
      title={job?.title || "Zlecenie"}
      subtitle={job ? `${toPolishDateTime(job.startAt)} -> ${toPolishDateTime(job.endAt)}` : "Ładowanie szczegółów"}
      actions={
        <div className="toolbar-row toolbar-row-detail">
          <button type="button" className="ghost-button toolbar-button-wide" onClick={() => navigate("/calendar")}>
            <ArrowLeft size={16} />
            <span>Wróć</span>
          </button>
          {job ? (
            <button type="button" className="primary-button toolbar-button-wide" onClick={() => setEditing(true)}>
              <Pencil size={16} />
              <span>Edytuj</span>
            </button>
          ) : null}
          {job ? (
            <button
              type="button"
              className="ghost-button danger-button toolbar-button-wide"
              onClick={handleDeleteJob}
              disabled={deletingJob}
            >
              <Trash2 size={16} />
              <span>{deletingJob ? "Usuwanie..." : "Usuń"}</span>
            </button>
          ) : null}
        </div>
      }
    >
      {error ? <div className="form-error page-error">{error}</div> : null}
      {loading ? <div className="card-section">Ładowanie zlecenia...</div> : null}

      {!loading && job ? (
        <>
          <section className="detail-grid">
            <article className="card-section">
              <div className="section-header">
                <div>
                  <h2>Szczegóły</h2>
                  <p>Kluczowe informacje o zleceniu.</p>
                </div>
              </div>
              <dl className="detail-list">
                <div>
                  <dt>Klient</dt>
                  <dd>{job.customerName || "brak"}</dd>
                </div>
                <div>
                  <dt>Telefon</dt>
                  <dd>{job.customerPhone || "brak"}</dd>
                </div>
                <div>
                  <dt>Adres</dt>
                  <dd>{job.address || "brak"}</dd>
                </div>
                <div>
                  <dt>Start</dt>
                  <dd>{toPolishDateTime(job.startAt)}</dd>
                </div>
                <div>
                  <dt>Koniec</dt>
                  <dd>{toPolishDateTime(job.endAt)}</dd>
                </div>
                <div>
                  <dt>Ekipa</dt>
                  <dd>
                    {job.assignedUserIds.length
                      ? users
                          .filter((user) => job.assignedUserIds.includes(user.id))
                          .map((user) => user.name || user.email)
                          .join(", ")
                      : "brak"}
                  </dd>
                </div>
              </dl>
            </article>

            <article className="card-section">
              <div className="section-header">
                <div>
                  <h2>Zakres prac</h2>
                  <p>Opis wykonania i zakresu rezerwacji.</p>
                </div>
              </div>
              <div className="scope-body">{job.scope}</div>
            </article>
          </section>

          <AttachmentList
            token={token}
            jobId={id}
            attachments={job.attachments}
            uploading={uploading}
            deletingAttachmentId={deletingAttachmentId}
            onUpload={handleUpload}
            onDownload={(attachment) => downloadJobAttachment(token, id, attachment.id, attachment.originalName)}
            onDelete={handleDeleteAttachment}
          />

          <JobChat
            token={token}
            jobId={id}
            user={user}
            users={users}
            messages={job.messages}
            sending={sending}
            onSend={handleSendMessage}
            onEdit={handleEditMessage}
            onDownload={(attachment) => downloadJobAttachment(token, id, attachment.id, attachment.originalName)}
          />
        </>
      ) : null}

      {editing && job ? (
        <JobFormModal
          job={job}
          seedDate={null}
          defaultTitle=""
          users={users}
          saving={saving}
          onClose={() => setEditing(false)}
          onSave={handleSave}
        />
      ) : null}
    </AppShell>
  );
}
